import React, { useState, useEffect, useRef } from "react";
import { IoChevronDown, IoSearch, IoFolderOutline, IoClose } from "react-icons/io5";
import axios from "axios";
import { API_BASE_URL } from "utils/api";
import { getToken } from "utils/auth"; // Assuming getToken exists, fallback to localStorage

const FolderDropdown = ({ type = "Client", value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [folders, setFolders] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [hasAnyFolders, setHasAnyFolders] = useState(true);
  
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchFolders = async (search = "", pageNum = 1, append = false) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token") || (typeof getToken === 'function' ? getToken() : '');
      const response = await axios.get(`${API_BASE_URL}/folders/search`, {
        params: {
          q: search,
          page: pageNum,
          type,
          limit: 10,
        },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      const data = response.data?.data;
      if (!data) return;

      const matched = data.matched?.data || [];
      const remaining = data.remaining?.data || [];
      const newFolders = [...matched, ...remaining];
      
      if (search === "" && pageNum === 1) {
        setHasAnyFolders(newFolders.length > 0);
      }

      setFolders(append ? [...folders, ...newFolders] : newFolders);
      
      // If we have an initial value but no selectedFolder details, try to find it
      if (value && !selectedFolder && !append) {
        const found = newFolders.find(f => f._id === value);
        if (found) setSelectedFolder(found);
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchFolders(searchTerm, 1, false);
      setPage(1);
    }
  }, [isOpen, searchTerm]);

  // Initial fetch just to get the selected folder name if value is provided, and also populate list
  useEffect(() => {
    fetchFolders("", 1, false);
    if (!value) {
      setSelectedFolder(null);
    }
  }, [value]);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && !isLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchFolders(searchTerm, nextPage, true);
    }
  };

  const handleSelect = (folder) => {
    setSelectedFolder(folder);
    onChange(folder?._id || "");
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedFolder(null);
    setSearchTerm("");
    onChange("");
  };

  return (
    <div className="relative min-w-[200px]" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 shadow-sm transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
      >
        <div className="flex items-center gap-2 truncate">
          <IoFolderOutline className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
          <span className="truncate">
            {selectedFolder ? selectedFolder.name : "All Folders"}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {selectedFolder && (
            <div 
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-md dark:hover:bg-navy-600 cursor-pointer"
            >
              <IoClose className="h-3 w-3 text-gray-500" />
            </div>
          )}
          <IoChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 w-full min-w-[240px] rounded-xl border border-gray-200 bg-white py-2 shadow-lg dark:border-white/10 dark:bg-navy-700">
          {hasAnyFolders && (
            <div className="px-3 pb-2">
              <div className="relative">
                <IoSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search folders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 w-full rounded-md border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
            </div>
          )}
          
          <div 
            className="max-h-60 overflow-y-auto px-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-navy-600"
            onScroll={handleScroll}
            ref={listRef}
          >
            {hasAnyFolders && (
              <button
                onClick={() => handleSelect(null)}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                  !selectedFolder 
                    ? "bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 font-medium" 
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-navy-600"
                }`}
              >
                <IoFolderOutline className="h-4 w-4 opacity-70" />
                All Folders
              </button>
            )}
            
            {folders.length > 0 ? (
              folders.map((folder) => (
                <button
                  key={folder._id}
                  onClick={() => handleSelect(folder)}
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors mt-1 ${
                    selectedFolder?._id === folder._id
                      ? "bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 font-medium"
                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-navy-600"
                  }`}
                >
                  <IoFolderOutline className="h-4 w-4 opacity-70 shrink-0" />
                  <span className="truncate">{folder.name}</span>
                </button>
              ))
            ) : (
              !isLoading && (
                <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No folders found
                </div>
              )
            )}
            
            {isLoading && (
              <div className="py-3 text-center text-sm text-brand-500">
                Loading...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderDropdown;
